import React from 'react';

import { Button } from 'react-bootstrap';
import * as Sentry from '@sentry/browser';

import Widget from '../widget/widget';
import NeoLayout from '../components/neoLayout';
import NeoLoader from '../components/neoLoader';
import Http404 from '../components/http404';
import TokenErrorPage from '../components/tokenError';
import fetcher from '../fetcher/fetch';
import get_url_query from '../utils/url';
import Storage from '../components/localStorage';

import { useTranslation } from 'react-i18next';
import useSentryScope from '../utils/useSentryScope';
import { useBeforeunload, useInterval } from '../hooks/hooks';
import { Analytics } from '../components/google_analytics/analytics';
import Style from '../components/style';
import { TokenContext } from '@weezevent/connect';
import {hasFragmentPreview} from "../components/connect/neoConnect";
import { getParentUrl } from '../utils/getParentUrl';
import { WeezSocket } from '@weezevent/piglet';
import { inIframe } from '../utils/scroll';

const track_widget_access = (widgetKey) => {
    Analytics.pageview(`/widget/${widgetKey}`);
};

export default function SessionManager({ uuid, widgetKey, lang = null, changeLanguage = () => {}, onWidget = () => { } }) {

    const storage_key = `wz-queue-${widgetKey}`;
    let storage = React.useRef(new Storage(storage_key)).current;

    let [token, setToken] = React.useState(null);
    let [widget, setWidget] = React.useState(null);
    let [currentStep, setCurrentStep] = React.useState(null);
    let [widgetNotFound, setWidgetNotFound] = React.useState(false);
    let [errorMessage, setErrorMessage] = React.useState('');
    let [parentUrlFetched, setParentUrlFetched] = React.useState(false);
    let _tokenContext = React.useContext(TokenContext);

    useSentryScope('widget_key', widgetKey);

    const another_tab_is_active = React.useCallback(() => {
        return Boolean(storage.activeTab() && !storage.isActiveTab(uuid) && storage.hasLocalValue());
    }, [storage, uuid]);

    // Check localStorage every second to see if another tab has taken control over this widget
    const check_local_storage = React.useCallback(() => {
        if (another_tab_is_active()) {
            // if another tab is active we update our token and step
            // both to keep this tab to date and to trigger re-renders
            let { token, step, widget } = storage.retrieve();
            setToken(token);
            setCurrentStep(step);
            setWidget(widget);
            onWidget(widget);
        }
    }, [onWidget, storage, another_tab_is_active]);

    useInterval(check_local_storage, 1000);
    let repeatGetWidget = 1;
    // Get the actual widget
    const getWidget = React.useCallback(async () => {
        const query = get_url_query();
        const params = new URLSearchParams(query)
        const urlToken = params.get("token");

        let headers = {
            'Accept-Language': lang,
        };

        if (urlToken || token) {
            headers['Authorization'] = `Bearer ${urlToken || token}`;
        } else {
            // If preview mode and connect token, inject it to allow the preview mode.
            if (hasFragmentPreview() && _tokenContext.token) {
                headers['Authorization'] = `Bearer ${_tokenContext.token}`;
            }
        }
        if (urlToken) {
            if (inIframe()) {
                try {
                    WeezSocket.send(
                    {
                        type: "clean-url-params",
                        origin: "neo",
                        payload: {
                            // Also clean magic link because the token can be send through this param.
                            // It can trigger infinite reload if we use the magic link in a iframe.
                            paramsKeys: ['token', 'magic_link'],
                            reload: false,
                        },
                    },
                    { needs_answer: false },
                    );
                } catch {
                    // eslint-disable-next-line no-console
                    console.error("Cannot clean the parent URL, check the iframe is properly integrated.");
                }
            } else {
                const url = new URL(window.location.href);
                url.searchParams.delete('token');
                window.history.replaceState({}, document.title, url.toString());
            }
        }
        if (!parentUrlFetched) {
            const parentUrl = await getParentUrl();
            if (parentUrl) {
                headers['X-Parent-URL'] = parentUrl;
            }
            setParentUrlFetched(true);
        }

        fetcher.get(`/ticket/widgets/${widgetKey}${query}`, { headers }).then(({ response }) => {
            // Handle platform specific tasks
            storage.write({
                uuid,
                step: response.first_step,
                widget: response,
                token: response.first_step.token
            });

            // Extra info
            Analytics.initializeOrgaTracker(response.tracking?.gaid);

            track_widget_access(widgetKey);

            // Continue with UI and logic
            setWidget(response);
            setCurrentStep(response.first_step);
            setToken(response.first_step.token);
            onWidget(response);
        }).catch(e => {
            setErrorMessage(e.response);
            Sentry.configureScope(scope => {
                scope.setExtra('token', token);
            });
            storage.remove();

            // if we can't find widget show 404
            if (e.status_code && e.status_code === 404) {
                setWidgetNotFound(true);
            } else {
                // we can retry later
                if (repeatGetWidget < 6) {
                    setTimeout(() => {
                        getWidget();
                    }, repeatGetWidget * 2000);
                    repeatGetWidget += 1;
                }
            }
        });
    }, [token, onWidget, lang, widgetKey, uuid, storage, repeatGetWidget, _tokenContext]);

    // Handles resuming session. Should only appear if another tab is active
    // and token is valid
    const onValidate = React.useCallback(() => {
        let { last_update, widget, token, step } = storage.retrieve();
        const query = get_url_query();
        const urlToken = query?.split('token=')[1];
        token = urlToken|| token
        storage.write({
            widget,
            step,
            token,
            uuid,
            last_update // use last last-update to not flaw countdown timings
        });

        Analytics.initializeOrgaTracker(widget?.tracking?.gaid);

        // Use data from localStorage
        setWidget(widget);
        setCurrentStep(step);
        setToken(token);

        if (urlToken) {
            getWidget()
        }
    }, [uuid, storage]);

    const _onStep = React.useCallback((step, leaving = false) => {
        // Google Analytics
        Analytics.pageview(`/widget/${widgetKey}/${step.slug}`);

        // Sentry
        Sentry.configureScope(scope => {
            scope.setExtra('widget_step', step.slug);
            scope.setExtra('step', step);
        });

        // Write new step to localstorage
        storage.write({
            uuid,
            step,
            widget,
            token: step.token
        });


        // Update info for manager
        setWidget(widget);
        setCurrentStep(step);
        setToken(step.token);
    }, [setToken, widget, uuid, widgetKey, storage]);

    // Whenever we quit the window we must perform some maintenance on
    // localStorage to ensure compatibility with both methods of session keeping
    // - coming back to the tab, and - having multiple tabs open
    useBeforeunload(
        React.useCallback(() => {
            // Only execute if step is not final
            let { last_update } = storage.retrieve();

            // If we are not the current tab we don't need to do anything else
            if (!storage.isActiveTab(uuid)) {
                return;
            }

            // We are the active tab
            // Disable this tab from 'active tabs'
            if (!currentStep || currentStep.is_final) {
                return storage.remove();
            } else {
                storage.write({
                    uuid: null, // no active tab
                    step: currentStep,
                    widget,
                    token,
                    last_update
                });
            }
        }, [currentStep, storage, token, widget, uuid])
    );

    // Executed when we leave the page and step is final
    const onLeave = React.useCallback(() => {
        storage.remove();
    }, [storage]);

    // Check for the first time if we can get the token and widget
    React.useEffect(() => {
        // if we have a stored widget and stored_uuid is not ours, we keep the token and ask to reload
        if (another_tab_is_active()) {
            return;
        }

        // if there is no valid local value (ex: token expired) and not our tab, restart
        if (!storage.hasLocalValue() && !storage.isActiveTab(uuid)) {
            return getWidget();
        }

        // if there is a local value, and tab is already ours
        // we are probably replaying this useEffect because of language change
        if (storage.hasLocalValue() && storage.isActiveTab()) {
            return;
        }

        // Else there is no local value, we can get the widget
        return getWidget();
    }, []); // eslint-disable-line
    // }, [ lang ]); // We will re-enable lang as a dependency when we split the widget/steps route

    const tokenError = React.useMemo(() => {
        if (errorMessage) {
            if (errorMessage.split(':"')[1]?.startsWith('Token is not valid, Signature has expired')) {
                return 'invalidToken';
            } else if (errorMessage.split(':"')[1]?.startsWith('Token is not valid, Invalid header string')) {
                return 'unknownToken';
            } else {
                return '';
            }
        }
    }, [errorMessage]);

    // Only show session screen if there is an active tab and it's different from this one
    let shouldResumeSession = another_tab_is_active();
    if (shouldResumeSession && widget) {
        return <ResumeSession onValidate={onValidate} widget={widget} lang={lang} />;
    }

    // Show 404 if needed
    if (widgetNotFound) {
        return <Http404 />;
    }
    // 403 - invalid or expired token
    if (tokenError) {
        return <TokenErrorPage tokenError={tokenError} storage={storage} />
    }
    // Show loader while getting widget
    if (!widget || !currentStep) {
        return <NeoLoader />;
    }

    // Render actual widget
    return <Widget
        changeLanguage={changeLanguage}
        widgetKey={widgetKey}
        onStep={_onStep}
        widget={widget}
        onLeave={onLeave}
        lang={lang}
        storage={storage}
        token={token}
        step={currentStep}
    />;
}

const getClosestLanguage = (lang = null, langs = []) => {
    if (!lang) {
        return null;
    }
    let language = null;
    //Order of priority
    //1) Equality (fr==fr or fr-FR ==fr-FR)
    //2) Language to test derived from language in wording
    //3) Language from wording derived from language to test or both are derived from same language
    for (let i = 0; i < langs.length; i++) {
        let _lang = langs[i];
        //If equal, same language => return it
        // fr-FR  and fr-fr or fr and fr
        if (lang.toLowerCase() === _lang.toLowerCase()) {
            return _lang
        }
        //Test if derived from language in wording
        //fr-FR and fr
        if (lang.length > _lang.length) {
            if (lang.toLowerCase().substring(0, 2) === _lang.toLowerCase()) {
                language = _lang;
            }
        }

        //Test if they are both derived from same language
        //en-GB and en-CA
        if (lang.length === _lang.length && lang.length === 5) {
            if ((lang.toLowerCase().substring(0, 2) === _lang.toLowerCase().substring(0, 2)) && !language) {
                language = _lang;
            }
        }
        //Test if it's parent of the language in wording
        //en and en-GB
        if (lang.length < _lang.length) {
            if ((lang.toLowerCase().substring(0, 2) === _lang.toLowerCase().substring(0, 2)) && !language) {
                language = _lang;
            }
        }
    }
    return language;
}


const handleWidgetWording = (widget, lang, key) => {
    if (!lang || !widget || !key) {
        return null;
    }
    let wording = widget?.customs?.wording;
    if (!wording) {
        return null;
    }
    let languages = Object.keys(wording);
    //Get closest language in wording from
    let closestLanguage = getClosestLanguage(lang, languages);
    return wording?.[closestLanguage]?.[key];
}

/**
 * Render the modal of exisiting session.
 * @param {*} param0
 */
export function ResumeSession({ onValidate, widget = null, lang = null }) {
    const { t } = useTranslation();
    const key = 'continue';
    const { title, description, button } = handleWidgetWording(widget, lang, key) || {};
    return (
        <>
            <Style style={widget.customs.css} />
            <NeoLayout>
                <NeoLayout.Header config={{ title: title ?? t('queue.continue.title') }} />
                <NeoLayout.Description config={{ description: description ?? t('queue.continue.description') }} />
                <Button id='wz-button-continue' variant='primary' onClick={onValidate}>{button ?? t('queue.continue.valide')}</Button>
            </NeoLayout>
        </>
    )
}
